import { forwardRef } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from './Button.constants';
import { styles } from './Button.styles';
import { ButtonProps } from './Button.types';
import { withPersonalizedButtonStyle } from './withPersonalizedButtonStyle';
import { TBaseButtonChildrenProps } from '@photosi/rn-components/buttons/base-button/BaseButton.types';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { ButtonIconChild } from '@photosi/rn-components/buttons/base-button-children/button-icon-child/ButtonIconChild';
import { mergeStyles } from 'src/_shared/utils/mergeStyles';
import { ButtonTextChild } from '@photosi/rn-components/buttons/base-button-children/button-text-child/ButtonTextChild';
import { BaseButton } from '@photosi/rn-components/buttons/base-button/BaseButton';

const BUTTON_SIZE_STYLES_MAP = {
  [BUTTON_SIZES.SMALL]: {
    button: styles.buttonSmall,
    buttonInner: styles.buttonInnerSmall,
    icon: styles.iconSmall,
    text: styles.textSmall,
    textHover: styles.textHover,
    textDisabled: styles.textDisabled,
    textFocus: styles.textFocus,
    textPressed: styles.textPressed,
    iconHover: styles.iconHover,
    iconDisabled: styles.iconDisabled,
    iconPressed: styles.iconPressed,
  },
  [BUTTON_SIZES.REGULAR]: {
    button: styles.buttonDefault,
    buttonInner: styles.buttonInnerDefault,
    icon: styles.iconDefault,
    text: styles.textDefault,
    textHover: styles.textHover,
    textDisabled: styles.textDisabled,
    textFocus: styles.textFocus,
    textPressed: styles.textPressed,
    iconHover: styles.iconHover,
    iconDisabled: styles.iconDisabled,
    iconPressed: styles.iconPressed,
  },
};

const ButtonComponent = forwardRef(
  (
    {
      size = BUTTON_SIZES.REGULAR,
      type = BUTTON_TYPES.DEFAULT,
      iconName,
      iconTestID: iconTestIDProp,
      ...buttonProps
    }: ButtonProps,
    ref,
  ) => {
    const {
      testID,
      style,
      innerStyle,
      textStyle,
      textHoverStyle,
      textDisabledStyle,
      textPressedStyle,
      textFocusStyle,
      iconStyle,
      iconHoverStyle,
      iconDisabledStyle,
      iconPressedStyle,
      disabledStyle,
      focusStyle,
      hoverStyle,
      pressedStyle,
      textTestID: textTestIDProp,
      textEllipsizeMode,
      textNumberOfLines,
      text,
      loading,
      layout = BUTTON_LAYOUTS.HUG,
      badgeRenderer,
    } = buttonProps;

    const textTestID = textTestIDProp ?? (testID ? `${testID}.text` : 'button.text');
    const iconTestID = iconTestIDProp ?? (testID ? `${testID}.icon` : 'button.icon');

    const sizeStyles = BUTTON_SIZE_STYLES_MAP[size];

    const borderWidth = StyleSheet.flatten(style)?.borderWidth ?? 0;
    const borderCompensationStyle = borderWidth
      ? {
          paddingVertical: (sizeStyles?.button.paddingVertical ?? 0) - borderWidth,
          paddingHorizontal: (sizeStyles?.button.paddingHorizontal ?? 0) - borderWidth,
        }
      : undefined;

    const buttonStyle = mergeStyles(
      sizeStyles?.button,
      borderCompensationStyle,
      style,
      layout === BUTTON_LAYOUTS.HUG
        ? styles.buttonHug
        : layout === BUTTON_LAYOUTS.CENTER
          ? styles.buttonCenter
          : styles.buttonFill,
    );
    const buttonInnerStyle = mergeStyles(sizeStyles?.buttonInner, innerStyle);

    const mergedTextStyle = mergeStyles(sizeStyles?.text, textStyle, { opacity: loading ? 0 : 1 });
    const mergedTextHoverStyle = mergeStyles(sizeStyles?.textHover, textHoverStyle);
    const mergedTextDisabledStyle = mergeStyles(sizeStyles?.textDisabled, textDisabledStyle);
    const mergedTextPressedStyle = mergeStyles(sizeStyles?.textPressed, textPressedStyle);
    const mergedTextFocusStyle = mergeStyles(sizeStyles?.textFocus, textFocusStyle);

    const mergedIconStyle = mergeStyles(sizeStyles?.icon, iconStyle, { opacity: loading ? 0 : 1 });
    const mergedIconHoverStyle = mergeStyles(sizeStyles?.iconHover, iconHoverStyle);
    const mergedIconDisabledStyle = mergeStyles(sizeStyles?.iconDisabled, iconDisabledStyle);
    const mergedIconPressedStyle = mergeStyles(sizeStyles?.iconPressed, iconPressedStyle);

    return (
      <BaseButton
        ref={ref}
        {...buttonProps}
        accessibilityLabel={buttonProps.accessibilityLabel ?? (typeof text === 'string' ? text : undefined)}
        // aria-busy=true while loading — screen readers announce the button is processing
        accessibilityState={{ ...buttonProps.accessibilityState, busy: !!loading }}
        style={buttonStyle}
        innerStyle={buttonInnerStyle}
        disabledStyle={disabledStyle}
        focusStyle={focusStyle}
        hoverStyle={hoverStyle}
        pressedStyle={pressedStyle}
      >
        {(baseButtonChildrenProps: TBaseButtonChildrenProps) => {
          const iconColor = Array.isArray(iconStyle)
            ? (iconStyle[iconStyle.length - 1] as any)?.color
            : (iconStyle as any)?.color;
          if (type === BUTTON_TYPES.DEFAULT) {
            return (
              <>
                <ButtonTextChild
                  {...{
                    text,
                    textStyle: mergedTextStyle,
                    textDisabledStyle: mergedTextDisabledStyle,
                    textFocusStyle: mergedTextFocusStyle,
                    textHoverStyle: mergedTextHoverStyle,
                    textPressedStyle: mergedTextPressedStyle,
                    textTestID,
                    textEllipsizeMode,
                    textNumberOfLines,
                    ...baseButtonChildrenProps,
                  }}
                />
                {loading && (
                  <View style={styles.buttonAbsolute}>
                    <ActivityIndicator size="small" color={iconColor as string} />
                  </View>
                )}
              </>
            );
          }
          if (type === BUTTON_TYPES.ICON_LEFT && iconName) {
            return (
              <View style={styles.wrapper}>
                <ButtonIconChild
                  {...{
                    iconName: iconName as ICON_NAMES,
                    iconStyle: mergedIconStyle,
                    iconDisabledStyle: mergedIconDisabledStyle,
                    iconHoverStyle: mergedIconHoverStyle,
                    iconPressedStyle: mergedIconPressedStyle,
                    iconTestID,
                    badgeRenderer,
                    ...baseButtonChildrenProps,
                  }}
                />
                <ButtonTextChild
                  {...{
                    text,
                    textStyle: mergedTextStyle,
                    textDisabledStyle: mergedTextDisabledStyle,
                    textFocusStyle: mergedTextFocusStyle,
                    textHoverStyle: mergedTextHoverStyle,
                    textPressedStyle: mergedTextPressedStyle,
                    textTestID,
                    textEllipsizeMode,
                    textNumberOfLines,
                    ...baseButtonChildrenProps,
                  }}
                />
                {loading && (
                  <View style={styles.buttonAbsolute}>
                    <ActivityIndicator size="small" color={iconColor as string} />
                  </View>
                )}
              </View>
            );
          }
          return null;
        }}
      </BaseButton>
    );
  },
);

ButtonComponent.displayName = 'ButtonComponent';

export const Button = withPersonalizedButtonStyle(ButtonComponent, styles);
